import { useEffect } from "react";
import { useConfig } from "@kaltura/mediaspace-shared-data-kms-config/useConfig";
import LocalIframe from "../local-iframe/LocalIframe";
import PreviewEmbedProps from "../PreviewEmbedProps";
import { useEventHandler } from "@kaltura/mediaspace-shared-hooks";

/**
 * Preview Media embed in an iframe
 *
 * an iframe is used to load the relevant player lib without interfering
 * with the host page.
 *
 * Supports both Kaltura entries and YouTube videos.
 */
export function PreviewEmbed(props: PreviewEmbedProps) {
    const {
        width = 432,
        height = 243,
        title,
        partnerId,
        uiConfId,
        entryId,
        youtubeId,
        ks,
        serviceUrl,
        cdnUrl = "https://cdnapisec.kaltura.com",
        startTime,
        endTime,
        loadThumbnailWithKs = false,
        onPlay,
        onHasAudio,
    } = props;

    const {
        application: { cspNonce },
    } = useConfig();

    const playerLib = `${cdnUrl}/p/${partnerId}/embedPlaykitJs/uiconf_id/${uiConfId}`;

    // Base configuration for the player
    const config = {
        targetId: "kaltura_player",
        provider: { partnerId, uiConfId, ks, loadThumbnailWithKs },
        env: { serviceUrl, cdnUrl },
        playback: { startTime, endTime },
    };

    let script;

    // Handle YouTube videos if youtubeId is provided
    if (youtubeId) {
        console.log("[PreviewEmbed] Rendering YouTube video with ID:", youtubeId);
        // Create external source object for YouTube
        const externalSourceObj = {
            progressive: [
                {
                    mimetype: "video/youtube",
                    url: youtubeId,
                },
            ],
        };

        script = `
        try {
            var kalturaPlayer = KalturaPlayer.setup(${JSON.stringify(config)});
            kalturaPlayer.setMedia({
                sources: ${JSON.stringify(externalSourceObj)}
            });`;
    }
    else {
        // Regular Kaltura entry
        script = `
        try {
            var kalturaPlayer = KalturaPlayer.setup(${JSON.stringify(config)});
            kalturaPlayer.loadMedia({entryId:"${entryId}"});`;
    }

    script
        += `kalturaPlayer.addEventListener(kalturaPlayer.Event.UI.USER_CLICKED_PLAY, event => {
                parent.postMessage({id: "${uiConfId}"}, '*');
            });
            parent.postMessage({id: "${uiConfId}", hasAudio: kalturaPlayer.plugins.audioPlayer !== undefined}, '*');
        } catch (e) {
            console.error(e.message)
        };`;

    const content = `
        <div class="chromatic-ignore">
            <div id="kaltura_player"></div>
            <script src="${playerLib}"></script>
            <script nonce="${cspNonce}">${script}</script>
        </div>`;
    const handleOnPlay = useEventHandler(onPlay);
    const handleOnHadAudio = useEventHandler(onHasAudio);

    useEffect(() => {
        const handlePlay = (ev: any) => {
            if (ev.data && ev.data.id === uiConfId) {
                handleOnPlay?.();
                window.removeEventListener("message", handlePlay);
            }
        };
        window.addEventListener("message", handlePlay);

        const handleHasAudio = (ev: any) => {
            if (ev.data && ev.data.hasAudio !== undefined && ev.data.id === uiConfId) {
                handleOnHadAudio?.(ev.data.hasAudio);
                window.removeEventListener("message", handleHasAudio);
            }
        };
        window.addEventListener("message", handleHasAudio);

        return () => {
            window.removeEventListener("message", handlePlay);
            window.removeEventListener("message", handleHasAudio);
        };
    }, [handleOnPlay, handleOnHadAudio, uiConfId]);

    return <LocalIframe content={content} title={title} width={width} height={height} />;
}
export default PreviewEmbed;
